import styled, { css } from "styled-components";
import Grid from "assets/homepage/landing/Grid2.svg";
import { typography } from "screens/Homepage/components/typography";
import { BlueHighlightedCSS, LinkMarketingButton } from "screens/Homepage/styles";
import { docsLink, dashboardLink, demoLink } from "constants/Links";
import { ReactComponent as YCombinatorLogo } from "assets/y-combinator.svg";
import { useState, useEffect, useRef } from "react";
import RiveWrapper from "./RiveAnimation";
import RiveWrapperComponent from "components/RiveAnimation";
import CodeSwitcher from "./CodeSwitcher";
import NavLink from "components/NavLink";
import { sandboxDemoLink } from "constants/Links";

const RIVE_FILE_PATH = "/beam_v2.riv";
const STATE_MACHINE_NAME = "State Machine 1";

const Hero: React.FC = () => {
  const [showAnimation, setShowAnimation] = useState(true);
  const [animateContent, setAnimateContent] = useState(false);
  const riveAnimationRef = useRef<any>(null);

  useEffect(() => {
    setAnimateContent(true);
  }, []);

  return (
    <Container>
      <AnimationBackground>
        <RiveWrapperComponent
          stateMachine={STATE_MACHINE_NAME}
          riveFilePath="/beam.riv"
          artboard="hero"
          onRiveInit={rive => {
            riveAnimationRef.current = rive;
          }}
        />
      </AnimationBackground>
      <ContentWrapper>
        <MainInfo $animate={animateContent}>
          <TitleWrapper>
            <HighlightedTitle $text="AI Infrastructure">AI Infrastructure</HighlightedTitle>
            <Title>For Developers</Title>
          </TitleWrapper>
          <SubTitle>
            Run sandboxes, inference, and training with ultrafast boot times, instant autoscaling, and a
            developer experience that just works.
          </SubTitle>
          <ButtonGroup>
            <LinkMarketingButton href={dashboardLink} size="lg">
              Get Started
            </LinkMarketingButton>
            <LinkMarketingButton
              href={demoLink}
              newWindow
              buttonTheme="secondary"
              variant="outline"
              size="lg"
            >
              Talk to an Engineer
            </LinkMarketingButton>
          </ButtonGroup>
          <LearnMoreContainer>
            <Link href={sandboxDemoLink} newWindow>
              Learn more about sandboxes ⟶
            </Link>
          </LearnMoreContainer>
          {/* <BadgeContainer>
            <Text>Backed by</Text>
            <YCombinatorLogo height="40" />
          </BadgeContainer> */}
        </MainInfo>
        <RiveContainer>
          {/* <CodeSwitcher /> */}

          <RiveWrapper />
        </RiveContainer>
      </ContentWrapper>
    </Container>
  );
};

export default Hero;

const AnimationBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  overflow: hidden;

  & > div {
    width: 100%;
    height: 100%;
  }

  canvas {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
  }
`;

const ContentWrapper = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 64px;
      width: 100%;
      max-width: 1400px;
      @media (max-width: ${theme.breakpoints.lg}px) {
        flex-direction: column;
        gap: 48px;
      }

      @media (max-width: ${theme.breakpoints.md}px) {
        gap: 40px;
      }
    `;
  }}
`;

const RiveContainer = styled.div`
  ${({ theme }) => {
    return css`
      width: 100%;
      height: 520px;
      border-radius: 24px;
      display: flex;
      margin-right: -48px;

      @media (max-width: ${theme.breakpoints.xl}px) {
        width: 100%;
        height: 480px;
      }

      @media (max-width: ${theme.breakpoints.lg}px) {
        display: none;
      }
    `;
  }}
`;

const BadgeContainer = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      align-items: center;
      padding: 0px;
      margin-top: 24px;
    `;
  }}
`;

const Text = styled.p`
  ${({ theme }) => {
    return css`
      color: ${theme.colors.gray10};
      ${typography.textLgLight};
    `;
  }}
`;

const Link = styled(NavLink)`
  && {
    ${props => props.theme.styleSet.textBase};
    color: ${props => props.theme.colors.blue11};
    cursor: pointer;
    padding: 3px;

    :hover {
      text-decoration: underline;
      color: ${props => props.theme.colors.blue11};
    }
  }
`;

const LearnMoreContainer = styled.div`
  ${({ theme }) => {
    return css`
      margin-top: 40px;
    `;
  }}
`;

const Container = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      flex-direction: column;
      align-items: center;
      box-sizing: border-box;
      isolation: isolate;
      width: 100%;
      gap: 16px;
      background-image: url(${Grid});
      background-size: cover;
      background-position: bottom;
      background-repeat: no-repeat;
      border-bottom: 1px solid rgba(255, 255, 255, 0.04);
      padding: 48px 16px 64px 16px;

      @media (min-width: ${theme.breakpoints.sm}px) {
        padding: 48px 32px 64px 32px;
      }

      @media (min-width: ${theme.breakpoints.md}px) {
        padding: 48px 48px 64px 48px;
      }

      @media (min-width: ${theme.breakpoints.lg}px) {
        padding: 64px 32px 80px 32px;
      }

      @media (min-width: ${theme.breakpoints.xl}px) {
        padding: 64px 64px 80px 64px;
      }
    `;
  }}
`;

const MainInfo = styled.div<{ $animate?: boolean }>`
  ${({ theme, $animate }) => {
    return css`
      display: flex;
      flex-direction: column;
      width: 100%;
      align-items: flex-start;
      text-align: left;
      padding: 0px;
      margin-left: 24px;
      opacity: ${$animate ? 1 : 0};
      transform: translateY(${$animate ? 0 : "40px"});
      transition: opacity 0.8s ease-out, transform 0.8s ease-out;

      @media (max-width: ${theme.breakpoints.lg}px) {
        align-items: center;
        text-align: center;
        margin-top: 0px;
        margin-left: 0px;
      }
    `;
  }}
`;

const TitleWrapper = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      text-align: left;

      @media (max-width: ${theme.breakpoints.lg}px) {
        align-items: center;
        text-align: center;
      }
    `;
  }}
`;

const Title = styled.h1`
  ${({ theme }) => {
    return css`
      all: unset;
      margin-bottom: 24px;
      color: ${theme.colors.gray12};
      text-align: center;
      text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
      max-width: 100%;
      ${typography.title40};
      @media (min-width: ${theme.breakpoints.sm}px) {
        ${typography.title48};
      }
      @media (min-width: ${theme.breakpoints.md}px) {
        ${typography.title56};
      }
      @media (min-width: ${theme.breakpoints.lg}px) {
        ${typography.title48};
        text-align: left;
      }
      @media (min-width: ${theme.breakpoints.xl}px) {
        ${typography.title56};
        text-align: left;
      }
    `;
  }}
`;

const HighlightedTitle = styled(Title)<{ $text?: string }>`
  ${({ theme }) =>
    BlueHighlightedCSS(
      theme.colors.gray1,
      `0px 4px 96px rgba(83, 113, 231, 0.64), 0px 8px 192px rgba(83, 113, 231, 0.48),
          0px 0px 8px rgba(132, 157, 255, 0.48)`
    )};
`;

const SubTitle = styled.p`
  ${({ theme }) => {
    return css`
      all: unset;
      margin-bottom: 48px;
      color: ${theme.colors.gray11};
      max-width: 750px;
      ${typography.textLgLight};

      @media (min-width: ${theme.breakpoints.sm}px) {
        ${typography.textXlLight};
      }
    `;
  }}
`;

const ButtonGroup = styled.div`
  ${({ theme }) => {
    return css`
      display: grid;
      grid-gap: 16px;
      justify-items: center;
      grid-template-columns: auto auto;

      @media (max-width: ${theme.breakpoints.sm}px) {
        width: 100%;
        grid-template-columns: 1fr;
        a,
        button {
          width: 100%;
        }
      }
    `;
  }}
`;

const Badge = styled.button`
  margin-bottom: 20px;
  height: 60px;
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  background-color: #1b1b1d;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 20px;
    padding: 2px;
    background: linear-gradient(90deg, #5371e7, #84a4ff);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: destination-out;
    mask-composite: exclude;
  }
`;

const BadgeText = styled.p`
  ${({ theme }) => {
    return css`
      color: ${theme.colors.gray11};

      ${typography.textSmLight};
      font-weight: 500;
      text-align: center;
      color: white;
      margin: auto;
    `;
  }}
`;
